import { bindActionCreators } from 'redux';
import { compact, flow, join, upperFirst, values } from 'lodash/fp';
import { connect } from 'react-redux';
import { P, Strong } from '@ahmdigital/ui';
import React from 'react';

import { DRAWER_IDS, PAYMENT_FREQUENCY_LABEL } from '../../ahm-constants/ui';
import { ExtrasComparerWrapper, FilterBarWrapper, FlexWrapper, StyledButton, StyledDiv, Wrapper } from './styles';
import { ProductType } from '../../types/product';
import { RootState } from '../../reducers';
import drawerActionCreators from '../../actions/drawer';
import getIsExtrasComparerPage from '../../utils/get-is-extras-comparer-page';
import selectCustomer from '../../selectors/select-customer';
import selectCustomerHumanReadable from '../../selectors/select-customer-human-readable';

type FilterBarProps = {
  customer: {
    frequency: string;
    hospitalExcess: string;
    rebate: boolean;
  };
  customerReadable: {
    ages: {
      client: number;
      spouse?: number;
    };
    incomeTier: string;
    scale: string;
    state: string;
  };
  needsProductType?: ProductType['extrasType'];
  needsType?: string;
  openChangeDetailsDrawer: (...args: unknown[]) => unknown;
};

const FilterBar = ({
  customer,
  customerReadable,
  needsProductType,
  needsType,
  openChangeDetailsDrawer,
}: FilterBarProps) => {
  const { frequency, hospitalExcess, rebate } = customer;
  const { ages, incomeTier, scale, state } = customerReadable;

  const customerAges = flow(values, join(' and '))(ages);

  const priceBasedOnSentence = flow(compact, join(', '))([scale, state, customerAges]);

  const incomeSentence = rebate
    ? `${scale}, ${incomeTier} per ${PAYMENT_FREQUENCY_LABEL.yearly}`
    : 'I don’t want to claim the rebate';

  const isExtrasPage = getIsExtrasComparerPage({ needsProductType, needsType });

  return (
    <FilterBarWrapper>
      <FlexWrapper>
        <Wrapper>
          <StyledDiv>
            <Strong>*Price based on</Strong>
            <P>{upperFirst(priceBasedOnSentence)}</P>
          </StyledDiv>
          <StyledDiv>
            <Strong>Income</Strong>
            <P>{upperFirst(incomeSentence)}</P>
          </StyledDiv>
          <ExtrasComparerWrapper fullWidth={isExtrasPage}>
            <StyledDiv>
              <Strong>Paying</Strong>
              <P>{upperFirst(frequency)}</P>
            </StyledDiv>
            {!isExtrasPage && (
              <StyledDiv>
                <Strong>Excess</Strong>
                <P>${hospitalExcess}</P>
              </StyledDiv>
            )}
          </ExtrasComparerWrapper>
        </Wrapper>

        <StyledButton
          icon={{
            name: 'pencil',
            size: 'extraSmall',
          }}
          onClick={openChangeDetailsDrawer}
          variant={['purple']}
        >
          Refine Price
        </StyledButton>
      </FlexWrapper>
    </FilterBarWrapper>
  );
};

FilterBar.defaultProps = {
  needsProductType: null,
  needsType: null,
};

const mapStateToProps = (state: RootState) => ({
  customer: selectCustomer(state),
  customerReadable: selectCustomerHumanReadable(state),
});

// @ts-expect-error - Automatic, Please fix when editing this file
const mapDispatchToProps = (dispatch) => {
  const drawerActions = bindActionCreators(drawerActionCreators, dispatch);

  return {
    openChangeDetailsDrawer: () => drawerActions.openDrawer(DRAWER_IDS.CHANGE_DETAILS),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FilterBar);

export { FilterBar as FilterBarForTest };
